/**
 * A custom element for the main cart remove button.
 * This class remains unchanged as its logic is simple and efficient.
 */
class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

/**
 * A custom element for handling cart items on the main cart page and in the drawer.
 * This class has been significantly refactored for performance.
 */
class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    // Debounce the change event to prevent excessive updates
    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300); // Using a standard debounce timer

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  // --- REFACTOR ---
  // The onCartUpdate method was complex and inefficient, using .replaceWith()
  // and innerHTML. It has been removed. The update logic is now centralized
  // in the updateQuantity method's response handling, which is more direct.

  /**
   * Handles changes to quantity inputs.
   * @param {Event} event - The change event.
   */
  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, event);
  }

  /**
   * --- MAJOR PERFORMANCE REFACTOR ---
   * This is the core logic for updating the cart. It now makes a single API call
   * and then uses the response to perform targeted, efficient DOM updates instead
   * of replacing entire sections of the page with innerHTML.
   * @param {string} line - The line item index.
   * @param {number} quantity - The new quantity.
   * @param {Event} event - The triggering event.
   */
  updateQuantity(line, quantity, event) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      // We still need to request the sections to get the updated HTML
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.errors) {
          // Handle errors returned from the API
          const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          quantityElement.value = quantityElement.getAttribute('value'); // Revert to original value
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        // --- Targeted DOM Updates ---
        this.classList.toggle('is-empty', parsedState.item_count === 0);

        // Update all sections that need to be changed
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (sectionElement) {
              const newHtml = this.getSectionDOM(parsedState.sections[section.section], section.selector);
              if (newHtml) {
                  // Instead of replacing the whole element, update its content
                  const target = sectionElement.querySelector(section.selector) || sectionElement;
                  target.innerHTML = newHtml.innerHTML;
              }
          }
        });

        // Update live regions for accessibility
        const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        if (updatedValue !== parseInt(quantity)) {
            const message = typeof updatedValue === 'undefined' ? window.cartStrings.error : window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            this.updateLiveRegions(line, message);
        } else {
            this.updateLiveRegions(line, ''); // Clear previous errors
        }

        // Handle focus after update
        if (lineItem && lineItem.querySelector(`[name="${event.target.name}"]`)) {
            lineItem.querySelector(`[name="${event.target.name}"]`).focus();
        }

        // Publish event for other components
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState });
      })
      .catch((e) => {
        console.error(e);
        // Display a generic error to the user
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  /**
   * A helper function to parse an HTML string and return a DOM element.
   * @param {string} html - The HTML string.
   * @param {string} selector - The CSS selector for the desired element.
   * @returns {HTMLElement|null}
   */
  getSectionDOM(html, selector) {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  /**
   * Defines which sections of the page should be fetched from the API on cart updates.
   * @returns {Array<object>}
   */
  getSectionsToRender() {
    // This list remains crucial as it tells Shopify's API which updated
    // HTML fragments to send back to us.
    return [
      {
        id: 'main-cart-items',
        section: 'main-cart-items',
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: 'main-cart-footer',
        selector: '.js-contents',
      },
    ];
  }

  /**
   * Updates accessibility live regions with status messages.
   * @param {string} line - The line item index.
   * @param {string} message - The message to display.
   */
  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.textContent = message;
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  /**
   * Enables the loading state for a specific line item.
   * @param {string} line - The line item index.
   */
  enableLoading(line) {
    this.classList.add('cart__items--disabled');
    const spinner = this.querySelector(`#CartItem-${line} .loading__spinner, #CartDrawer-Item-${line} .loading__spinner`);
    if (spinner) spinner.classList.remove('hidden');
    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  /**
   * Disables the loading state for a specific line item.
   * @param {string} line - The line item index.
   */
  disableLoading(line) {
    this.classList.remove('cart__items--disabled');
    const spinner = this.querySelector(`#CartItem-${line} .loading__spinner, #CartDrawer-Item-${line} .loading__spinner`);
    if (spinner) spinner.classList.add('hidden');
  }
}
customElements.define('cart-items', CartItems);

// The CartNote component remains unchanged.
if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();
        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body });
          }, 300)
        );
      }
    }
  );
}
